import { defineNuxtPlugin, useNuxtApp } from 'nuxt/app';
import defaultHuDateFormat from '../utils/locales/date_formats/hu.json';
import defaultEnDateFormat from '../utils/locales/date_formats/en.json';

const defaultDateFormat: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour12: false,
  hour: 'numeric',
  minute: 'numeric',
};

const defaultDateFormatByLocale: Record<string, Intl.DateTimeFormatOptions> = {
  hu: defaultHuDateFormat as Intl.DateTimeFormatOptions,
  en: defaultEnDateFormat as Intl.DateTimeFormatOptions,
};

function createPluginProvides () {
  const nuxtApp = useNuxtApp();
  const { $i18n, $logger } = nuxtApp;

  const dateFunction = (
    entity: string | Date,
    localOptions: Intl.DateTimeFormatOptions | undefined,
    {
      removeLastDot,
      removeSpaces,
      removeAllDot,
    }: {
      removeLastDot?: boolean,
      removeSpaces?: boolean,
      removeAllDot?: boolean
    } = {},
  ): string => {
    const options = localOptions || defaultDateFormatByLocale[$i18n.locale.value] || defaultDateFormat;

    try {
      let d = new Date(entity).toLocaleString($i18n.locale.value, options);
      if (removeLastDot) {
        d = d.replace(/\.$/, '');
      }
      if (removeSpaces) {
        d = d.replace(/\s/g, '');
      }
      if (removeAllDot) {
        d = d.replace(/\./g, '');
      }
      return d;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return entity.toString();
    }
  };

  /**
   * format: YYYY-MM-DD
   * @description this is just the standard toISOString splitted to only
   * include the date and timezoneoffset is recalculated.
   * */
  const dateISO8601Function = (d: Date): string | undefined => {
    if (!(d instanceof Date)) {
      $logger.error(TypeError('dateRangeOfDays expexts an array of Date objects'));
      return;
    }
    const o = d.getTimezoneOffset();
    return (new Date(d.getTime() - (o * 60 * 1000))).toISOString().split('T')[0];
  };

  /**
   * Loops through all the days between the max and min of `dateArray`.
   */
  const dateRangeOfDaysFunction = function* (dateArray: Date[] = [], ignoreTime: boolean = true): Generator<Date, void, unknown> {
    for (const d of dateArray) {
      if (!(d instanceof Date)) {
        $logger.error(TypeError('dateRangeOfDays expexts an array of Date objects'));
        return;
      }
    }
    const loop = new Date(Math.min(...dateArray.map(date => date.getTime())));
    const to = new Date(Math.max(...dateArray.map(date => date.getTime())));
    if (ignoreTime) {
      loop.setHours(0);
      loop.setMinutes(0);
      loop.setSeconds(0);
      loop.setMilliseconds(0);
      to.setHours(0);
      to.setMinutes(0);
      to.setSeconds(0);
      to.setMilliseconds(0);
    }
    while (loop <= to) {
      yield (new Date(loop));
      loop.setDate(loop.getDate() + 1);
    }
  };

  return {
    date: dateFunction,
    dateISO8601: dateISO8601Function,
    dateRangeOfDays: dateRangeOfDaysFunction,
  };
}

export type DatePluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});
